import { useState, useEffect, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Package, Plus } from 'lucide-react'
import { listProducts, getTenantInfo, updateProduct } from '../api/products'
import { useLangPrefix } from '../hooks/useLangPrefix'
import DesktopShell from '../components/layout/DesktopShell'
import AppShell from '../components/layout/AppShell'
import useManagerStore from '../store/managerStore'
import { canAccess, FULL_ACCESS, normalizeRole, RBAC_MODULES } from '../lib/rbac'
import { formatMoney } from '../lib/money'
import { useTenantCurrencyState } from '../hooks/useTenantCurrency'

const getSellingPrice = (product) => {
    const priceCandidates = [
        product?.final_display_price,
        product?.pricing?.sale_price,
        product?.pricing?.base_price,
        product?.price,
    ]
    for (const candidate of priceCandidates) {
        const parsedPrice = Number(candidate)
        if (Number.isFinite(parsedPrice) && parsedPrice >= 0) return parsedPrice
    }
    return 0
}
const getProductImageUrl = (product) => {
    if (!product || typeof product !== 'object') return ''
    if (typeof product.media?.primary_image_url === 'string' && product.media.primary_image_url.trim()) return product.media.primary_image_url
    const firstImage = Array.isArray(product.images) ? product.images[0] : null
    if (typeof firstImage === 'string' && firstImage.trim()) return firstImage
    if (firstImage && typeof firstImage === 'object' && typeof firstImage.url === 'string' && firstImage.url.trim()) return firstImage.url
    if (typeof product.image_url === 'string' && product.image_url.trim()) return product.image_url
    return ''
}
const normalizeSearchText = (value) => {
    if (!value) return ''
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .toLowerCase()
        .trim()
}

const INDUSTRY_CATEGORIES = {
    fnb: ['starter','main_course','dessert','beverage','alcohol','combo','set_menu','takeaway'],
    spa: ['massage','facial','body_treatment','nail','hair','package','membership','add_on'],
    hotel: ['room_standard','room_deluxe','room_suite','breakfast','airport_transfer','laundry','spa_access','dining_credit','early_checkin'],
    travel: ['day_tour','multi_day_tour','transfer','activity','visa_service','travel_insurance','accommodation_upgrade','guide_service'],
    education: ['course_full','course_module','trial_class','private_lesson','group_class','material_pack','exam_prep','membership_monthly'],
}
const PAGE_SIZE = 10

export default function ProductsPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { prefix, lang } = useLangPrefix()
    const tenantId = useManagerStore((s) => s.tenantId)
    // Catalog prices use the tenant currency, shown only once resolved
    // (unresolved means the VND placeholder).
    const { currency, isResolved: currencyResolved } = useTenantCurrencyState()
    const formatPrice = (amount) => (currencyResolved
        ? formatMoney(amount, currency, lang)
        : <span role="status" aria-label={t('common.loading')}>…</span>)
    const [products, setProducts] = useState([])
    const [tenantIndustry, setTenantIndustry] = useState('fnb')
    const [loading, setLoading] = useState(true)
    const [fetchError, setFetchError] = useState(false)
    const [search, setSearch] = useState('')
    const [filterCategory, setFilterCategory] = useState('')
    const [filterActive, setFilterActive] = useState('')
    const [page, setPage] = useState(0)
    const [confirmState, setConfirmState] = useState({ open: false, mode: null, product: null })
    const [confirmLoading, setConfirmLoading] = useState(false)
    const isDesktop = window.innerWidth >= 1024
    const storeRole = useManagerStore((s) => s.role)
    const role = normalizeRole(storeRole)
    const canManageProducts = canAccess(role, RBAC_MODULES.products, FULL_ACCESS)

    const fetchProducts = useCallback(async () => {
        if (!tenantId) {
            setProducts([])
            setLoading(false)
            return
        }
        setLoading(true)
        setFetchError(false)
        try {
            // Never request seeding here: listing must not create products as a side effect.
            const params = {}
            if (filterCategory) params.category = filterCategory
            if (filterActive !== '') params.active = filterActive === 'true'
            const prodData = await listProducts(tenantId, params)
            setProducts(prodData.products || [])
        } catch (e) {
            Sentry.captureException(e, { tags: { action: 'fetch_products' } })
            setFetchError(true)
        } finally {
            setLoading(false)
        }
    }, [filterCategory, filterActive, tenantId])

    useEffect(() => { fetchProducts() }, [fetchProducts])

    // Tenant industry only drives the predefined category filter options.
    useEffect(() => {
        if (!tenantId) {
            setTenantIndustry('fnb')
            return
        }
        getTenantInfo(tenantId)
            .then(data => setTenantIndustry(data.industry_type || 'fnb'))
            .catch(() => setTenantIndustry('fnb'))
    }, [tenantId])

    async function handleDeactivate(p) {
        if (!canManageProducts) return
        setConfirmState({ open: true, mode: 'deactivate', product: p })
    }

    async function handleRestore(p) {
        if (!canManageProducts) return
        setConfirmState({ open: true, mode: 'restore', product: p })
    }

    async function handleConfirmAction() {
        const { mode, product } = confirmState
        if (!mode || !product || !tenantId) return
        setConfirmLoading(true)
        try {
            await updateProduct(tenantId, product.id, { active: mode === 'restore' })
            fetchProducts()
        } catch (err) {
            Sentry.captureException(err, {
              tags: { action: 'toggle_product_active' },
              extra: { tenantId, productId: product.id, mode },
            })
        } finally {
            setConfirmLoading(false)
            setConfirmState({ open: false, mode: null, product: null })
        }
    }

    function closeConfirmModal() {
        if (confirmLoading) return
        setConfirmState({ open: false, mode: null, product: null })
    }

    const normalizedQuery = normalizeSearchText(search)
    const predefinedCategories = INDUSTRY_CATEGORIES[tenantIndustry] || []
    const dynamicCategories = Array.from(new Set((products || []).map((p) => (p.category || '').trim()).filter(Boolean)))
    const categoryOptions = [...predefinedCategories, ...dynamicCategories.filter((c) => !predefinedCategories.includes(c))]
    const filtered = products.filter((p) => {
        if (!normalizedQuery) return true
        const normalizedName = normalizeSearchText(p.name || '')
        if (normalizedName.includes(normalizedQuery)) return true
        return (p.keywords || []).some((k) => normalizeSearchText(k).includes(normalizedQuery))
    })
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
    const safePage = Math.min(page, totalPages - 1)
    const pageStart = safePage * PAGE_SIZE
    const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE)
    // The catalog is truly empty only when nothing narrows the result set.
    const hasActiveFilters = Boolean(normalizedQuery || filterCategory || filterActive !== '')
    // A failed fetch also leaves products empty, so it must not count as an empty catalog.
    const isCatalogEmpty = products.length === 0 && !hasActiveFilters && !fetchError

    useEffect(() => {
        setPage(0)
    }, [search, filterCategory, filterActive, tenantId])

    useEffect(() => {
        if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1))
    }, [page, totalPages])

    const content = (
        <div style={{ padding: isDesktop ? '24px 32px' : '16px', background: 'var(--gc-page-bg)', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <div style={{ fontSize: isDesktop ? 22 : 18, fontWeight: 700, color: '#111' }}>{t('products.title')}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{t('products.itemCount', { count: filtered.length })}</div>
                </div>
                {canManageProducts && !(isCatalogEmpty && !loading) && (
                    <button onClick={() => navigate(`${prefix}/products/new`)}
                        style={{ padding: '8px 18px', background: 'var(--gc-primary)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Plus size={14} />
                        {t('products.addProduct')}
                    </button>
                )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('products.searchPlaceholder')}
                    style={{ flex: 1, minWidth: 160, padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', outline: 'none' }} />
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                    style={{ padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', outline: 'none' }}>
                    <option value="">{t('products.allCategories')}</option>
                    {categoryOptions.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
                <select value={filterActive} onChange={e => setFilterActive(e.target.value)}
                    style={{ padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', outline: 'none' }}>
                    <option value="">{t('products.allStatus')}</option>
                    <option value="true">{t('common.active')}</option>
                    <option value="false">{t('common.inactive')}</option>
                </select>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#999', fontSize: 14 }}>{t('products.loading')}</div>
            ) : fetchError ? (
                <div style={{ background: 'white', borderRadius: isDesktop ? 14 : 12, border: '0.5px solid #e0e3f0', padding: isDesktop ? 40 : 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 13, color: '#ef4444' }}>{t('products.loadError')}</div>
                    <button onClick={fetchProducts}
                        style={{ padding: '7px 16px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: 'pointer', color: '#333' }}>
                        {t('common.retry')}
                    </button>
                </div>
            ) : isCatalogEmpty ? (
                <div style={{ background: 'white', borderRadius: isDesktop ? 14 : 12, border: '0.5px solid #e0e3f0', padding: isDesktop ? '48px 24px' : '32px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--gc-page-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                        <Package size={26} color="#1535c8" />
                    </div>
                    <div style={{ fontSize: isDesktop ? 16 : 15, fontWeight: 600, color: '#111', marginBottom: 6 }}>{t('products.emptyTitle')}</div>
                    <div style={{ fontSize: 13, color: '#666', maxWidth: 360, lineHeight: 1.5 }}>{t(canManageProducts ? 'products.emptyHint' : 'products.emptyHintReadOnly')}</div>
                    {canManageProducts && (
                        <button onClick={() => navigate(`${prefix}/products/new`)}
                            style={{ marginTop: 18, padding: '9px 20px', background: 'var(--gc-primary)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Plus size={14} />
                            {t('products.emptyCta')}
                        </button>
                    )}
                </div>
            ) : isDesktop ? (
                <div style={{ background: 'white', borderRadius: 14, border: '0.5px solid #e0e3f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8f9ff' }}>
                                {['', t('products.headers.name'), t('products.headers.category'), t('products.headers.price'), t('products.headers.stock'), t('products.headers.status'), ...(canManageProducts ? [t('products.headers.actions')] : [])].map(h => (
                                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '0.5px solid #e0e3f0' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((p, i) => {
                                const imageUrl = getProductImageUrl(p)
                                return <tr key={p.id} style={{ borderBottom: i < pageItems.length - 1 ? '0.5px solid #f0f0f0' : 'none' }}>
                                    <td style={{ padding: '10px 16px', width: 72, minWidth: 72 }}>
                                        {imageUrl
                                            ? <img src={imageUrl} alt="" style={{ width: 40, minWidth: 40, height: 40, minHeight: 40, borderRadius: 6, objectFit: 'cover', display: 'block' }} />
                                            : <div style={{ width: 40, minWidth: 40, height: 40, minHeight: 40, borderRadius: 6, background: 'var(--gc-page-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Package size={18} color="#1535c8" /></div>
                                        }
                                    </td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{p.name}</div>
                                        {p.short_description && <div style={{ fontSize: 11, color: '#999', marginTop: 2, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.short_description}</div>}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#666' }}>{p.category}</td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gc-primary)' }}>{formatPrice(getSellingPrice(p))}</div>
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: 13, color: '#333' }}>{p.stock_quantity ?? '-'}</td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <span style={{ cursor: 'default', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: p.active ? '#dcfce7' : '#f3f4f6', color: p.active ? '#16a34a' : '#999' }}>
                                            {p.active ? t('common.active') : t('common.inactive')}
                                        </span>
                                    </td>
                                    {canManageProducts && (
                                        <td style={{ padding: '10px 16px' }}>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button onClick={() => navigate(`${prefix}/products/${p.id}/edit`)} style={{ padding: '5px 12px', border: '0.5px solid #e0e3f0', borderRadius: 6, background: 'white', fontSize: 12, cursor: 'pointer', color: '#333' }}>{t('common.edit')}</button>
                                                {p.active ? (
                                                    <button onClick={() => handleDeactivate(p)} style={{ padding: '5px 12px', border: '0.5px solid #fecaca', borderRadius: 6, background: 'white', fontSize: 12, cursor: 'pointer', color: '#ef4444' }}>{t('products.deactivate')}</button>
                                                ) : (
                                                    <button onClick={() => handleRestore(p)} style={{ padding: '5px 12px', border: '0.5px solid #bbf7d0', borderRadius: 6, background: 'white', fontSize: 12, cursor: 'pointer', color: '#16a34a' }}>{t('products.restore')}</button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            })}
                        </tbody>
                    </table>
                    {filtered.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 13 }}>{t('products.noProducts')}</div>}
                    {filtered.length > 0 && (
                        <div style={{ padding: '12px 16px', borderTop: '0.5px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: 12, color: '#666' }}>
                                {t('products.pagination.page', { current: safePage + 1, total: totalPages })}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                    disabled={safePage === 0}
                                    style={{ padding: '5px 12px', border: '0.5px solid #e0e3f0', borderRadius: 6, background: 'white', fontSize: 12, cursor: safePage === 0 ? 'not-allowed' : 'pointer', opacity: safePage === 0 ? 0.5 : 1 }}
                                >
                                    {t('products.pagination.prev')}
                                </button>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={safePage >= totalPages - 1}
                                    style={{ padding: '5px 12px', border: '0.5px solid #e0e3f0', borderRadius: 6, background: 'white', fontSize: 12, cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: safePage >= totalPages - 1 ? 0.5 : 1 }}
                                >
                                    {t('products.pagination.next')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {pageItems.map(p => {
                        const imageUrl = getProductImageUrl(p)
                        return <div key={p.id} onClick={canManageProducts ? () => navigate(`${prefix}/products/${p.id}/edit`) : undefined} style={{ background: 'white', borderRadius: 12, border: '0.5px solid #e0e3f0', padding: 14, display: 'flex', gap: 12, alignItems: 'center', cursor: canManageProducts ? 'pointer' : 'default' }}>
                            {imageUrl
                                ? <img src={imageUrl} alt="" style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                                : <div style={{ width: 52, height: 52, borderRadius: 8, background: 'var(--gc-page-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Package size={22} color="#1535c8" /></div>
                            }
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 2 }}>{p.name}</div>
                                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{p.category}</div>
                                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gc-primary)' }}>{formatPrice(getSellingPrice(p))}</div>
                            </div>
                            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: p.active ? '#dcfce7' : '#f3f4f6', color: p.active ? '#16a34a' : '#999', flexShrink: 0 }}>
                                {p.active ? t('common.active') : t('common.inactive')}
                            </span>
                        </div>
                    })}
                    {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#999', fontSize: 13 }}>{t('products.noProducts')}</div>}
                    {filtered.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                            <button
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={safePage === 0}
                                style={{ padding: '7px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: safePage === 0 ? 'not-allowed' : 'pointer', opacity: safePage === 0 ? 0.5 : 1 }}
                            >
                                {t('products.pagination.prev')}
                            </button>
                            <div style={{ fontSize: 12, color: '#666' }}>
                                {t('products.pagination.page', { current: safePage + 1, total: totalPages })}
                            </div>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={safePage >= totalPages - 1}
                                style={{ padding: '7px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: safePage >= totalPages - 1 ? 0.5 : 1 }}
                            >
                                {t('products.pagination.next')}
                            </button>
                        </div>
                    )}
                </div>
            )}
            {confirmState.open && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={(e) => { if (e.target === e.currentTarget) closeConfirmModal() }}
                >
                    <div style={{ width: '100%', maxWidth: 440, background: 'white', borderRadius: 14, boxShadow: '0 16px 40px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                        <div style={{ padding: '16px 18px', borderBottom: '0.5px solid #eef0f5', fontSize: 15, fontWeight: 700, color: '#111' }}>
                            {t('products.confirmTitle')}
                        </div>
                        <div style={{ padding: '14px 18px', fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                            {confirmState.mode === 'deactivate'
                                ? t('products.deactivateConfirm', { name: confirmState.product?.name || '' })
                                : t('products.restoreConfirm', { name: confirmState.product?.name || '' })}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '0 18px 16px 18px' }}>
                            <button
                                onClick={closeConfirmModal}
                                disabled={confirmLoading}
                                style={{ padding: '7px 12px', border: '0.5px solid #e5e7eb', borderRadius: 8, background: 'white', color: '#374151', fontSize: 13, cursor: confirmLoading ? 'not-allowed' : 'pointer', opacity: confirmLoading ? 0.6 : 1 }}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleConfirmAction}
                                disabled={confirmLoading}
                                style={{ padding: '7px 12px', border: 'none', borderRadius: 8, background: confirmState.mode === 'deactivate' ? '#ef4444' : '#16a34a', color: 'white', fontSize: 13, cursor: confirmLoading ? 'not-allowed' : 'pointer', opacity: confirmLoading ? 0.7 : 1 }}
                            >
                                {confirmLoading
                                    ? t('common.saving')
                                    : (confirmState.mode === 'deactivate' ? t('common.delete') : t('products.restore'))}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )

    return isDesktop
        ? <DesktopShell>{content}</DesktopShell>
        : <AppShell activeTab="products">{content}</AppShell>
}
